import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClaimWaitlistCode } from '../hooks/useApi';
import { WaitlistClaimError } from '../api/client';

/** Sentinel for the select: apply the code to every account in turn. */
const ALL_ACCOUNTS = 'all' as const;

export interface ClaimAccountOption {
  id: number;
  label: string;
}

interface Props {
  /** Dice's event id, straight off the waitlist entry group. */
  eventId: string;
  /**
   * The accounts on THIS waitlist — the only ones Dice could have mailed a code
   * to. One of them is always sent as `account_id`, so the endpoint's
   * "omit it if they have a single Dice account" fallback never has to carry
   * a customer with several.
   */
  accounts: ClaimAccountOption[];
  accountsPath: string;
  billingPath: string;
}

export const WaitlistClaimCode: React.FC<Props> = ({
  eventId,
  accounts,
  accountsPath,
  billingPath,
}) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  /** Which account to claim with, or ALL_ACCOUNTS to claim for every account. */
  const [target, setTarget] = useState<number | typeof ALL_ACCOUNTS>(
    accounts.length === 1 ? accounts[0].id : ALL_ACCOUNTS,
  );
  /** Accounts that successfully claimed the code after the whole queue finishes. */
  const [claimedWith, setClaimedWith] = useState<ClaimAccountOption[]>([]);
  const [failedWith, setFailedWith] = useState<{ account: ClaimAccountOption; message: string }[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [error, setError] = useState<WaitlistClaimError | Error | null>(null);
  /** 1-based position while working down the list, for the button's label. */
  const [attempt, setAttempt] = useState(0);

  const claimMutation = useClaimWaitlistCode();

  // No account on this waitlist means no code was sent to one, so there is
  // nothing here to offer.
  if (accounts.length === 0) return null;

  const trimmed = code.trim();
  const busy = claimMutation.isPending || attempt > 0;
  const canSubmit = !busy && trimmed.length > 0;

  const claimWith = async (account: ClaimAccountOption) =>
    claimMutation.mutateAsync({ event_id: eventId, code: trimmed, account_id: account.id });

  /** Apply the code to each selected account; a plan refusal stops the batch. */
  const handleClaim = async () => {
    if (!canSubmit) return;
    setError(null);
    setClaimedWith([]);
    setFailedWith([]);
    setSkippedCount(0);

    const queue = target === ALL_ACCOUNTS
      ? accounts
      : accounts.filter(a => a.id === target);

    const successes: ClaimAccountOption[] = [];
    const failures: { account: ClaimAccountOption; message: string }[] = [];
    let lastError: WaitlistClaimError | Error | null = null;

    for (let i = 0; i < queue.length; i++) {
      setAttempt(i + 1);
      try {
        const result = await claimWith(queue[i]);
        if (!result.success) {
          throw new Error(result.error || 'Failed to claim code');
        }
        successes.push(queue[i]);
      } catch (err: any) {
        lastError = err;
        failures.push({ account: queue[i], message: err.message || 'Failed to claim code' });
        if (err instanceof WaitlistClaimError && err.kind === 'plan') {
          break;
        }
      }
    }

    setAttempt(0);
    setClaimedWith(successes);
    setFailedWith(failures);
    setSkippedCount(queue.length - successes.length - failures.length);
    if (successes.length > 0) setCode('');
    setError(lastError);
  };

  /**
   * The message is Dice's own wherever the backend passed one through — "code
   * already used" says more than anything we'd write — so it goes out verbatim
   * and the hint below adds only what the seller can act on.
   */
  const hint = (() => {
    if (!(error instanceof WaitlistClaimError)) return null;
    switch (error.kind) {
      case 'dice_auth':
        return (
          <>
            Your Dice session expired.{' '}
            <Link to={accountsPath} className="text-blue-400 hover:underline">
              Reconnect your Dice account
            </Link>{' '}
            and try again.
          </>
        );
      case 'plan':
        return (
          <>
            Claiming codes needs Pro or Reserve.{' '}
            <Link to={billingPath} className="text-blue-400 hover:underline">
              See plans
            </Link>
          </>
        );
      case 'server':
        return <>Couldn't reach Dice. Try again.</>;
      default:
        // 400 and Dice's own rejections (used / invalid / expired code) are
        // already said better by the message above.
        return null;
    }
  })();

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 rounded text-xs text-blue-400 transition-colors hover:text-blue-300"
      >
        Have a claim code?
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
      {claimedWith.length > 0 ? (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="font-medium text-white">Claim summary</span>
              <span className="text-green-400">{claimedWith.length} claimed</span>
              {failedWith.length > 0 && <span className="text-[#fca5a5]">{failedWith.length} failed</span>}
              {skippedCount > 0 && <span className="text-white/50">{skippedCount} not attempted</span>}
            </div>
            <button
              type="button"
              onClick={() => { setClaimedWith([]); setFailedWith([]); setSkippedCount(0); setError(null); }}
              className="shrink-0 rounded px-2 py-1 text-xs text-white/50 transition-colors hover:bg-white/5 hover:text-white"
            >
              Claim another
            </button>
          </div>
          <details className="mt-2 text-xs">
            <summary className="w-fit cursor-pointer text-white/50 hover:text-white">Account details</summary>
            <ul className="mt-2 max-h-48 space-y-2 overflow-y-auto border-t border-white/10 pt-2">
              {claimedWith.map(account => (
                <li key={account.id} className="flex items-start justify-between gap-3">
                  <span className="min-w-0 break-words text-white/70">{account.label}</span>
                  <span className="shrink-0 text-green-400">Claimed</span>
                </li>
              ))}
              {failedWith.map(({ account, message }) => (
                <li key={account.id} className="flex items-start justify-between gap-3">
                  <span className="min-w-0 break-words text-white/70">{account.label}</span>
                  <span className="max-w-[50%] break-words text-right text-[#fca5a5]">{message}</span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault();
                  handleClaim();
                }
              }}
              placeholder="Claim code"
              aria-label="Dice claim code"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={busy}
              className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:border-[#3b82f6]/50 focus:outline-none"
            />

            {/* The endpoint takes one account per call, so "All accounts" is a
                walk down the list rather than a single broadcast request. */}
            {accounts.length > 1 && (
              <select
                value={target}
                onChange={e =>
                  setTarget(e.target.value === ALL_ACCOUNTS ? ALL_ACCOUNTS : Number(e.target.value))
                }
                aria-label="Dice account to claim with"
                disabled={busy}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white focus:border-[#3b82f6]/50 focus:outline-none"
              >
                <option value={ALL_ACCOUNTS}>All accounts</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.label}
                  </option>
                ))}
              </select>
            )}

            <button
              type="button"
              onClick={handleClaim}
              disabled={!canSubmit}
              className="rounded-lg bg-[#3b82f6] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#3b82f6]/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {!busy
                ? 'Claim'
                : target === ALL_ACCOUNTS && accounts.length > 1
                  ? `Trying ${attempt} of ${accounts.length}...`
                  : 'Claiming...'}
            </button>
          </div>

          {accounts.length > 1 && target === ALL_ACCOUNTS && !error && !busy && (
            <p className="mt-2 text-[11px] text-white/40">
              Applies the code to each Dice account in turn.
            </p>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="mt-2 break-words text-xs text-[#fca5a5]">
          {claimedWith.length > 0 && 'Some accounts could not claim the code. '}
          {error.message}
          {hint && <span className="mt-1 block text-white/50">{hint}</span>}
        </p>
      )}
    </div>
  );
};
